import _ from 'lodash';
import { CurrencyUtils, openrestHelpers, PropertiesUtils } from '@wix/wixrest-utils';
import moment from 'moment';
import { getMenuByItemId, getSectionByItemId } from './MenuUtils';
import { isManagerDiscount } from './ChargeUtils';

function getPrice(priceObject) {
  if (_.isUndefined(priceObject)) {
    return 0;
  } else if (_.isNumber(priceObject[1])) {
    return priceObject[1];
  } else {
    return priceObject;
  }
}

function getPriceStr(priceObject, currency, locale) {
  const n = getPrice(priceObject);
  return n === 0 ? '' : CurrencyUtils.toStr(n / 100, currency, locale);
}

function parseTime(time) {
  if (time === 'asap') {
    return moment();
  } else {
    throw new Error('Only time=asap is supported at the moment');
  }
}

export function createOrderItem({ itemId, itemsHash, time }) {
  const item = itemsHash[itemId];
  return { ...openrestHelpers.OrderItem.getOrderItemFromItem(item, itemsHash, parseTime(time)), labels: item.labels };
}

function getObjectItemByVariationPath(orderItem, variationPath) {
  let result = orderItem;
  _.without(variationPath, variationPath[variationPath.length - 1]).forEach(crumb => {
    if (crumb.type === 'index') {
      result = result.variationsChoices[crumb.value];
    } else if (crumb.type === 'itemId') {
      result = result.find(x => x.itemId === crumb.value);
    }
  });
  return result;
}

function mutateChoice({ orderItem, variation, variationIndex, choiceId, itemsHash, time }) {
  const price = getPrice(variation.prices[choiceId]);
  const choice = {
    ...openrestHelpers.OrderItem.getOrderItemFromItem(itemsHash[choiceId], itemsHash, parseTime(time)),
    price,
  };
  const choiceIndex = _.findIndex(orderItem.variationsChoices[variationIndex], x => _.isMatch(x, choice));
  if (variation.minNumAllowed === 1 && variation.maxNumAllowed === 1) {
    orderItem.variationsChoices[variationIndex] = [choice];
  } else if (choiceIndex === -1) {
    orderItem.variationsChoices[variationIndex].push(choice);
  } else {
    orderItem.variationsChoices[variationIndex] = _.without(
      orderItem.variationsChoices[variationIndex],
      orderItem.variationsChoices[variationIndex][choiceIndex],
    );
  }
}

export function toggleChoice({ orderItem, variationPath, choiceId, itemsHash, time }) {
  const clone = _.cloneDeep(orderItem);
  const variationOrderItem = getObjectItemByVariationPath(clone, variationPath);
  const variationIndex = _.last(variationPath).value;
  const variation = variationOrderItem.variations[variationIndex];
  mutateChoice({
    orderItem: variationOrderItem,
    variationIndex,
    variation,
    choiceId,
    itemsHash,
    time,
  });
  return clone;
}

export function changeCount({ orderItem, count }) {
  const result = _.cloneDeep(orderItem);
  result.count = count;
  return result;
}

export function orderItemChangeSpecialRequest({ orderItem, specialRequestValue }) {
  const result = _.cloneDeep(orderItem);
  result.specialRequest = specialRequestValue;
  return result;
}

function getVariationValue(variation, choices) {
  const isRadio = variation.minNumAllowed === 1 && variation.maxNumAllowed === 1;

  if (isRadio) {
    return choices[0] ? choices[0].itemId : null;
  } else {
    return null;
  }
}

function isVariationRadio(variation) {
  return variation.minNumAllowed === 1 && variation.maxNumAllowed === 1;
}

function parseVariationTitle({ titles, locale, full }) {
  if (titles.length === 1 || !full) {
    return titles[titles.length - 1][locale];
  } else {
    return titles
      .slice(1)
      .map(title => title[locale])
      .join(' / ');
  }
}

function getClientSideId(variation, suffix) {
  const result = `variation-${variation.variationPath.map(crumb => crumb.value).join('-')}`;
  if (suffix) {
    return result + '-' + suffix;
  } else {
    return result;
  }
}

function isVariationValid(variation, choices) {
  return choices.length >= (variation.minNumAllowed || 0) && choices.length <= variation.maxNumAllowed;
}

function getDisplayableChoices({ variation, choices, itemsHash, locale, currency, flat }) {
  return variation.itemIds.map(item => {
    const choice = choices.find(_choice => _choice.itemId === item.itemId);
    return {
      itemId: item.itemId,
      price: getPriceStr(variation.prices[item.itemId], currency, locale),
      title: itemsHash[item.itemId].title[locale],
      clientSideId: getClientSideId(variation, item.itemId),
      subVariations:
        choice && !flat
          ? choice.variations.map((_variation, index) => {
              return getDisplayableVariation({
                variation: _variation,
                itemsHash,
                currency,
                locale,
                choices: choice.variationsChoices[index],
                flat,
              });
            })
          : [],
    };
  });
}

function getDisplayableType(variation) {
  return variation.minNumAllowed === 1 && variation.maxNumAllowed === 1 ? 'variation.radio' : 'variation.checkbox';
}

function getDisplayableBaseVariation({ variation, itemsHash, currency, locale, choices, flat }) {
  return {
    type: getDisplayableType(variation),
    title: parseVariationTitle({ titles: variation.titles, locale, full: false }),
    fullTitle: parseVariationTitle({ titles: variation.titles, locale, full: true }),
    limitation: getVariationLimitation(variation),
    min: variation.minNumAllowed || 0,
    max: Math.min(variation.maxNumAllowed, 99999),
    isValid: isVariationValid(variation, choices),
    variationPath: variation.variationPath,
    clientSideId: getClientSideId(variation),
    choices: getDisplayableChoices({ variation, choices, itemsHash, locale, currency, flat }),
  };
}

function getDisplayableRadioVariation({ variation, itemsHash, currency, locale, choices, flat }) {
  return {
    ...getDisplayableBaseVariation({ variation, itemsHash, currency, locale, choices, flat }),
    value: getVariationValue(variation, choices),
  };
}

function getDisplayableCheckboxVariation({ variation, itemsHash, currency, locale, choices, flat }) {
  const checkedItemIds = _.keyBy(choices.map(c => c.itemId));
  const result = getDisplayableBaseVariation({ variation, itemsHash, currency, locale, choices, flat });
  result.choices = result.choices.map(choice => ({ ...choice, checked: Boolean(checkedItemIds[choice.itemId]) }));
  return result;
}

function getDisplayableVariation({ variation, itemsHash, currency, locale, choices, flat }) {
  if (isVariationRadio(variation)) {
    return getDisplayableRadioVariation({ variation, itemsHash, currency, locale, choices, flat });
  } else {
    return getDisplayableCheckboxVariation({ variation, itemsHash, currency, locale, choices, flat });
  }
}

export function flattenVariations({ orderItem, itemsHash }) {
  const variations = [];
  const variationsChoices = [];
  (orderItem.variations || []).forEach((variation, index) => {
    variations.push(variation);
    variationsChoices.push(orderItem.variationsChoices[index]);

    orderItem.variationsChoices[index].forEach(choice => {
      const innerOrderItem = flattenVariations({
        orderItem: choice,
        itemsHash,
      });

      variations.push(...innerOrderItem.variations);
      variationsChoices.push(...innerOrderItem.variationsChoices);
    });
  });

  return { variations, variationsChoices };
}

function applyMetaDataToVariations({ orderItem, variationPath = [], titles = [], itemsHash }) {
  const variations = (orderItem.variations || []).map((variation, index) => {
    const _variationPath = [...variationPath, { type: 'index', value: index }];
    const _titles = [...titles, variation.title];
    return { ...variation, variationPath: _variationPath, titles: _titles };
  });

  const variationsChoices = (orderItem.variationsChoices || []).map((choices, index) => {
    const variation = variations[index];
    const _variationPath = [...variationPath, { type: 'index', value: index }];
    const _titles = [...titles, variation.title];

    return choices.map(choice => {
      return applyMetaDataToVariations({
        orderItem: choice,
        variationPath: [..._variationPath, { type: 'itemId', value: choice.itemId }],
        titles: [..._titles, itemsHash[choice.itemId].title],
        itemsHash,
      });
    });
  });

  return { ...orderItem, variations, variationsChoices };
}

function getFlatDisplayableVariation({ orderItem, itemsHash, item, currency, locale }) {
  const { variations, variationsChoices } = flattenVariations({ orderItem, itemsHash });

  return variations.map((variation, index) => {
    const choices = variationsChoices[index];
    return getDisplayableVariation({ variation, item, itemsHash, currency, locale, choices, flat: true });
  });
}

function getNestedDisplayableVariation({ orderItem, itemsHash, item, currency, locale }) {
  return orderItem.variations.map((variation, index) => {
    const choices = orderItem.variationsChoices[index];
    return getDisplayableVariation({ variation, item, itemsHash, currency, locale, choices });
  });
}

function isVariationCollectionValid(variationsToDisplay) {
  return variationsToDisplay.every(
    variation =>
      variation.isValid && variation.choices.every(choice => isVariationCollectionValid(choice.subVariations)),
  );
}

function getFirstInvalidVariation(variationsToDisplay) {
  for (let i = 0; i < variationsToDisplay.length; i++) {
    const variation = variationsToDisplay[i];
    if (!variation.isValid) {
      return variation.clientSideId;
    }

    for (let j = 0; j < variation.choices.length; j++) {
      const invalidSubVariation = getFirstInvalidVariation(variation.choices[j].subVariations);
      if (invalidSubVariation) {
        return invalidSubVariation;
      }
    }
  }

  return null;
}

export function getDisplayableDishFromOrderItem({
  orderItem,
  itemsHash,
  locale,
  currency,
  flatten = true,
  time,
  deliveryType,
  simplifiedMenu,
}) {
  const _orderItem = applyMetaDataToVariations({ orderItem, itemsHash });
  const item = itemsHash[_orderItem.itemId];
  const base = {
    title: { type: 'title', value: item.title[locale] },
    subTitle: { type: 'subTitle', value: item.description[locale] },
    quantity: { type: 'quantity', value: _orderItem.count || 1 },
    specialRequest: { type: 'specialRequest', value: _orderItem.specialRequest || '' },
  };

  const variationsToDisplay = flatten
    ? getFlatDisplayableVariation({ orderItem: _orderItem, itemsHash, item, currency, locale })
    : getNestedDisplayableVariation({ orderItem: _orderItem, itemsHash, item, currency, locale });

  const isValid = isVariationCollectionValid(variationsToDisplay);

  const submitButton = {
    type: 'submit.button',
    value: getPriceStr(openrestHelpers.OrderItem.getTotalPrice(_orderItem), currency, locale),
    disabled: !isValid,
    highlightedError: getFirstInvalidVariation(variationsToDisplay),
  };

  const errors = getItemErrors(item, time || moment(), deliveryType, simplifiedMenu);

  return { ...base, variations: variationsToDisplay, submitButton, errors };
}

function getSimplifiedCondition(item) {
  const condition = _.get(item, 'condition', {});
  if (condition.hasOwnProperty('platforms')) {
    // Condition is already simplified, no need to do anything
    return condition;
  } else {
    return openrestHelpers.Condition.simplifyCondition(_.cloneDeep(item).condition);
  }
}

function getSingleItemErrors(item, time, deliveryType) {
  const errors = [];

  if (!item) {
    return [];
  }

  const { availability, isSoldOut, deliveryTypes } = getSimplifiedCondition(item);
  const { status } = openrestHelpers.Status.getStatus(availability, time);
  if (status === 'unavailable') {
    errors.push({ type: 'error.unavailable', availability });
  }
  if (isSoldOut) {
    errors.push({ type: 'error.soldout' });
  }
  if (deliveryType && !deliveryTypes[deliveryType]) {
    errors.push({ type: 'error.deliveryType', supportedDeliveryTypes: getItemDeliveryTypes(item) });
  }
  return errors;
}

export function getItemErrors(item, time, deliveryType, simplifiedMenu) {
  // First, check the item itself
  let result = getSingleItemErrors(item, time, deliveryType);

  if (simplifiedMenu && result.length === 0) {
    // In case the item if OK, proceed to the section it belongs to
    const section = getSectionByItemId(simplifiedMenu, item.id);
    result = getSingleItemErrors(section, time, deliveryType);
  }

  if (simplifiedMenu && result.length === 0) {
    // Then proceed to the menu that the section belongs to
    const menu = getMenuByItemId(simplifiedMenu, item.id);
    result = getSingleItemErrors(menu, time, deliveryType);
  }

  return result;
}

export function getItemDeliveryTypes(item) {
  const condition = getSimplifiedCondition(item);
  return Object.keys(condition.deliveryTypes).filter(dt => condition.deliveryTypes[dt]);
}

export function getVariationLimitation(variation) {
  const { minNumAllowed = 0, maxNumAllowed = 0 } = variation;
  const min = minNumAllowed;
  const max = maxNumAllowed > 1000 ? 0 : maxNumAllowed;
  if (min === 0 && max === 0) return '';
  if (min === 0 && max === 1) return UP_TO_ONE;
  if (min === 0 && max > 1) return UP_TO_MAX;
  if (min === 1 && max === 1) return EXACTLY_ONE;
  if (min === 1 && max === 0) return AT_LEAST_ONE;
  if (min > 1 && max === 0) return AT_LEAST_MIN;
  if (min === max) return EXACTLY;
  if (min > 1 && max > 1) return RANGE;
}

export const UP_TO_ONE = 'UP_TO_ONE';
export const UP_TO_MAX = 'UP_TO_MAX';
export const EXACTLY_ONE = 'EXACTLY_ONE';
export const AT_LEAST_ONE = 'AT_LEAST_ONE';
export const AT_LEAST_MIN = 'AT_LEAST_MIN';
export const EXACTLY = 'EXACTLY';
export const RANGE = 'RANGE';

export function getChoicesStr({ orderItem, itemsHash, i18n, locale, currency }) {
  return createCartOrderItem({ orderItem, itemsHash, i18n, locale, currency }).choicesStr;
}
export function createCartOrderItem({ orderItem, itemsHash, i18n, locale, currency }) {
  const { variations, variationsChoices } = openrestHelpers.OrderItem.flattenVariations({ orderItem });
  const fullOrderItem = openrestHelpers.OrderItem.getFullOrderItem({
    orderItem: { ...orderItem, variations, variationsChoices },
    itemsHash,
  });
  const localizedToStr = localized => (localized ? localized[locale] || _.values(localized)[0] : null);
  const stringifyMoney = amount => CurrencyUtils.toStr(amount / 100, currency, locale);
  const choices = openrestHelpers.OrderItem.itemVariationsAndCommentToStructure({
    fullOrderItem,
    stringifyMoney,
    i18n,
    localizedToStr,
  });

  const choicesStr = choices
    .map(d => d.text)
    .join(', ')
    .replace(/openrest_cartview_delimiter/g, ', ');

  return { fullOrderItem, choices, choicesStr };
}

function getAmountByGroupId({ menu, orderCharges }) {
  const orderChargeById = _(orderCharges)
    .keyBy('chargeId')
    .mapValues(value => value.amount || 0)
    .value();

  return _(menu.chargesV2)
    .groupBy(charge => PropertiesUtils.get(charge, 'groupId'))
    .mapValues(charges =>
      _(charges)
        .map(charge => orderChargeById[charge.id])
        .sum(),
    )
    .value();
}

function getRelevantCharges({ menu, deliveryTime, deliveryType, orderItems, source, platform }) {
  return _(menu.chargesV2)
    .filter(charge => charge.type === 'discount')
    .filter(charge =>
      openrestHelpers.ChargeV2.isDisplayable({
        charge,
        deliveryTime: parseTime(deliveryTime),
        deliveryType,
        orderItems,
        source,
        platform,
      }),
    )
    .value();
}

export function findByClientSideId(variations, clientSideId) {
  let result;
  const _variations = variations.variations || variations;

  for (let i = 0; i < _variations.length; i++) {
    const part = _variations[i];
    if (part.clientSideId === clientSideId) {
      result = part;
      break;
    }

    const choices = part.choices || [];

    for (let j = 0; j < choices.length; j++) {
      const tmp = findByClientSideId(choices[j].subVariations, clientSideId);
      if (tmp) {
        result = tmp;
      }
    }
  }

  return result;
}

export function getAllVariationClientSideIds(variations) {
  if (_.isEmpty(variations)) {
    return null;
  }

  const variationParts = variations.variations || variations;
  const topLevelIds = variationParts.map(part => part.clientSideId);
  const rec = _(variationParts)
    .map(part =>
      _(part.choices)
        .map(choice => getAllVariationClientSideIds(choice.subVariations))
        .flatten()
        .value(),
    )
    .flatten()
    .value();

  return _.compact([...topLevelIds, ...rec]);
}

export function getDisplayableDiscounts({
  menu,
  deliveryTime,
  deliveryType,
  orderItems,
  source,
  platform,
  orderCharges,
}) {
  const relevantCharges = getRelevantCharges({ menu, deliveryTime, deliveryType, orderItems, source, platform });
  const amountByGroupId = getAmountByGroupId({ menu, orderCharges });
  return _(relevantCharges)
    .map(charge => ({
      title: charge.title,
      description: charge.description,
      isManagerDiscount: isManagerDiscount(charge),
      groupId: PropertiesUtils.get(charge, 'groupId'),
    }))
    .uniqWith(_.isEqual)
    .map(charge => ({ ...charge, amount: amountByGroupId[charge.groupId] || 0 }))
    .filter(charge => !(charge.isManagerDiscount && charge.amount === 0))
    .value();
}

export function getInternalMenuAndItemsHash(menu) {
  //todo masha move to menu utils
  if (!menu) {
    return {};
  }

  const _menu = JSON.parse(JSON.stringify(menu));
  (_menu.items || []).forEach(function(_item) {
    openrestHelpers.MenuAmi.convertVariationsToInternalVariations(_item.variations || []);
  });
  // Using two copies of the menu, because openrestHelpers.MenuAmi.convertMenuToInternal mutates the menu
  const _disposableMenu = JSON.parse(JSON.stringify(_menu));
  const simplifiedMenu = openrestHelpers.MenuAmi.convertMenuToInternal({ menu: _disposableMenu });
  const _itemsHash = _.zipObject(_.map(simplifiedMenu.items, 'id'), simplifiedMenu.items);
  return { menu: _menu, itemsHash: _itemsHash, simplifiedMenu };
}

export function getLimitationString({ limitation, min, max, t }) {
  let key;
  switch (limitation) {
    case EXACTLY_ONE:
      key = 'phoneordering_menu_dish_chooseone_label';
      break;
    case UP_TO_ONE:
      key = 'phoneordering_menu_dish_chooseuptoone_label';
      break;
    case UP_TO_MAX:
      key = 'phoneordering_menu_dish_chooseupto_label';
      break;
    case AT_LEAST_ONE:
      key = 'phoneordering_menu_dish_chooseatleastone_label';
      break;
    case AT_LEAST_MIN:
      key = 'phoneordering_menu_dish_chooseatleast_label';
      break;
    case EXACTLY:
      key = 'phoneordering_menu_dish_choose_label';
      break;
    case RANGE:
      key = 'phoneordering_menu_dish_choosebetween_label';
      break;
    default:
      key = '';
      break;
  }

  return t(key, { min_amount: min, max_amount: max, amount: min });
}
